const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';
const TOKEN_KEY = 'monksheets_auth_token';
const IMPERSONATE_KEY = 'monksheets_impersonate_user';

function getImpersonateUserId(): string | null {
  try {
    const raw = sessionStorage.getItem(IMPERSONATE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ? String(parsed.id) : null;
  } catch {
    return null;
  }
}

export class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

// Token management
export function setApiToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function getApiToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearApiToken(): void {
  localStorage.removeItem(TOKEN_KEY);
  // Also clear the auth_logged_in indicator cookie so fetchUser doesn't
  // retry /auth/me after a 401 (which would cause an infinite redirect loop)
  document.cookie = 'auth_logged_in=; Max-Age=0; path=/';
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  headers?: Record<string, string>;
  params?: Record<string, string | number | boolean | undefined>;
}

async function request<T>(endpoint: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, params } = options;

  // Build URL with query params
  let url = `${API_BASE_URL}${endpoint}`;
  if (params) {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, String(value));
      }
    });
    const queryString = searchParams.toString();
    if (queryString) {
      url += `?${queryString}`;
    }
  }

  // Build headers with auth token if available
  const requestHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  const token = getApiToken();
  if (token) {
    requestHeaders['Authorization'] = `Bearer ${token}`;
  }

  const impersonateUserId = getImpersonateUserId();
  if (impersonateUserId) {
    requestHeaders['X-Impersonate-User'] = impersonateUserId;
  }

  const config: RequestInit = {
    method,
    headers: requestHeaders,
    credentials: 'include', // Keep for session fallback
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(url, config);

  if (!response.ok) {
    if (response.status === 401) {
      clearApiToken();
      // Notify authContext so it clears user state immediately (no page reload required)
      window.dispatchEvent(new CustomEvent('auth:expired'));
    }
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(
      errorData.message || `Request failed with status ${response.status}`,
      response.status,
      errorData.details
    );
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return {} as T;
  }

  return JSON.parse(text);
}

// Only get/post are still consumed (authContext); put/patch/delete and the
// uploadFile helper were removed once ProfilePage migrated to services/api/client.
// New code should use services/api/client.ts (apiGet/apiPost/apiPut/apiPatch/apiDelete).
export const api = {
  get: <T>(endpoint: string, params?: Record<string, string | number | boolean | undefined>) =>
    request<T>(endpoint, { method: 'GET', params }),

  post: <T>(endpoint: string, body?: unknown) => request<T>(endpoint, { method: 'POST', body }),
};
